import { Container } from '@/components/Container';
import { getPublished } from '@/data/loader';
import { useTranslation } from '@/hooks/useTranslation';
import { Link } from 'react-router-dom';
import googleThumbs from '../../../public/images/google/thumbs.json';

/* ═══════════════════════════════════════════
   PRAIAS — Cards com destaque por vila
   ═══════════════════════════════════════════ */

export function PraiasSection() {
  const { t } = useTranslation();
  const praias = getPublished('praia');
  const destaques = praias.slice(0, 6);

  return (
    <section id="praias" className="py-16 sm:py-20 bg-sand-50">
      <Container>
        <div className="text-center mb-12">
          <span className="font-body text-sm text-coral font-semibold uppercase tracking-wider">
            {t('guide.beaches.overline')}
          </span>
          <h2 className="font-display text-4xl sm:text-5xl text-warm-dark mt-2">
            {t('guide.beaches.title')}
          </h2>
          <p className="font-editorial text-lg text-warm-gray mt-2 max-w-2xl mx-auto">
            {t('guide.beaches.subtitle')}
          </p>
        </div>

        {/* Aviso de maré */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 mb-6">
          <span role="img" aria-label="Maré" className="text-xl flex-shrink-0">
            🌊
          </span>
          <p className="font-body text-sm text-warm-gray">
            <strong className="text-warm-dark">Atenção: </strong>
            algumas praias têm faixa de areia estreita na maré alta. Praias de mar aberto têm
            correntes de retorno — crianças devem ficar na área próxima ao mar calmo de Nova
            Brasília. Consulte a tábua de marés antes de planejar o dia.
          </p>
        </div>

        {/* Guia de escolha por perfil */}
        <div className="bg-sand-50 border border-sand-200 rounded-2xl p-5 mb-10">
          <h3 className="font-display text-xl text-warm-dark mb-4">
            Qual praia é a certa para você?
          </h3>
          <div className="grid sm:grid-cols-2 gap-3">
            {[
              { perfil: '🤿 Para mergulho e água cristalina', praia: 'Praia das Encantadas' },
              {
                perfil: '👨‍👩‍👧 Para família com crianças',
                praia: 'Praia de Nova Brasília (mar calmo)',
              },
              { perfil: '🌊 Para surfistas e ondas', praia: 'Praia do Farol (mar aberto)' },
              { perfil: '🥾 Com trilha panorâmica', praia: 'Praia de Fora (via trilha de areia)' },
            ].map(item => (
              <div key={item.perfil} className="flex items-start gap-2.5">
                <span className="text-sm text-warm-gray font-body">{item.perfil}:</span>
                <span className="text-sm text-teal-deep font-body font-semibold">{item.praia}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {destaques.map((praia: any) => (
            <Link
              key={praia.id}
              to={`/praias/${praia.handle}`}
              className="group bg-white rounded-2xl border border-sand-200 overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-md transition-shadow"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img
                  src={(googleThumbs as Record<string, string>)[praia.handle] || praia.image}
                  alt={praia.title}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-body text-xs text-teal-deep font-medium uppercase">
                    {praia.vilarejoName}
                  </span>
                  {praia.kidsFriendly && (
                    <span className="bg-teal-50 text-teal-deep text-xs font-body font-semibold px-2 py-0.5 rounded-full">
                      {t('guide.beaches.kidsFriendly')}
                    </span>
                  )}
                </div>
                <h3 className="font-display text-xl text-warm-dark mb-1">
                  {praia.title.toUpperCase()}
                </h3>
                <p className="font-body text-sm text-warm-gray mb-3 line-clamp-2">
                  {praia.subtitle}
                </p>
                <div className="flex items-center gap-3 text-xs font-body text-warm-gray">
                  <span className="flex items-center gap-1">
                    <svg
                      className="w-4 h-4 text-coral"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {praia.access}
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-8">
          <Link
            to="/praias-ilha-do-mel"
            className="inline-flex items-center gap-2 font-body text-sm font-semibold text-teal-deep hover:text-teal-700 transition-colors"
          >
            {t('guide.beaches.seeAll')}
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </Link>
        </div>
      </Container>
    </section>
  );
}
